import { InjectionToken } from '@angular/core';

/**
 * Web Storage-shaped abstraction. On the server, providers return a no-op
 * implementation so SSR rendering does not crash on missing localStorage.
 *
 * Use via `inject(LOCAL_STORAGE)` / `inject(SESSION_STORAGE)` instead of
 * accessing global `localStorage` / `sessionStorage` directly.
 */
export interface StorageRef {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

export const noopStorage: StorageRef = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
  clear: () => {},
};

const hasUsable = (s: unknown): s is StorageRef =>
  typeof s !== 'undefined' && typeof (s as StorageRef).getItem === 'function';

export const LOCAL_STORAGE = new InjectionToken<StorageRef>('LOCAL_STORAGE', {
  providedIn: 'root',
  factory: () => {
    const ref = typeof localStorage !== 'undefined' ? localStorage : undefined;
    return hasUsable(ref) ? ref : noopStorage;
  },
});

export const SESSION_STORAGE = new InjectionToken<StorageRef>('SESSION_STORAGE', {
  providedIn: 'root',
  factory: () => {
    const ref = typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;
    return hasUsable(ref) ? ref : noopStorage;
  },
});
